import React, { useEffect, useState } from 'react';
import { LoadingAnimatedInline } from '../components/Loading';
import { useAuth } from '../auth/AuthContext';
import {
  type AdminConfirmationPopupProps,
  AdminConfirmationPopup
} from '../components/AdminConfirmationPopup';

type Template = {
  id: string;
  name: string;
  html: string;
  parameters: string[];
};

type RemoteGroup = {
  id: string;
  name: string;
  users: string[];
};

const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const EmailUpdate: React.FC = () => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [templateParams, setTemplateParams] = useState<Record<string, string>>({});

  const [groups, setGroups] = useState<RemoteGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [customEmail, setCustomEmail] = useState<string>('');
  const [recipients, setRecipients] = useState<string[]>([]);

  useEffect(() => {
    fetchGroups();
    populateEmailTemplates();
  }, []);

  const fetchGroups = async () => {
    try {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      const res = await fetch('/api/admin/email/groups', {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'same-origin'
      });
      if (!res.ok) throw new Error('Failed to fetch groups');
      const data = await res.json();
      const loaded: RemoteGroup[] = data.groups || [];
      setGroups(loaded);
      if (loaded.length > 0) setSelectedGroup(loaded[0].id);
    } catch (e) {
      console.error('Error fetching email groups:', e);
    }
  };

  const populateEmailTemplates = async () => {
    try {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();

      const res = await fetch('/api/admin/email/getTemplate', {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'same-origin'
      });

      if (!res.ok) throw new Error('Could not get email templates from server.');

      const res_data = await res.json();
      const serverTemplates: Template[] = res_data.templates.map((t: any) => ({
        id: t.id,
        name:
          t.id === 'updateTemplate' ? 'Software Update'
          : t.id === 'patchTemplate' ? 'Software Patch'
          : t.id === 'generic' ? 'Generic'
          : t.id,
        html: t.htmlContent,
        parameters: t.parameters
      }));

      setTemplates(serverTemplates);
      if (serverTemplates.length > 0) setSelectedTemplateId(serverTemplates[0].id);
    } catch (err) {
      console.error('Could not populate email templates on TerraStation Update emails tab.');
    }
  };

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
  const recipientCount = recipients.length;

  const addGroupRecipients = () => {
    const group = groups.find((g) => g.id === selectedGroup);
    if (!group) return;
    setRecipients((prev) => {
      const next = [...prev];
      group.users.forEach((email) => {
        if (!next.includes(email)) next.push(email);
      });
      return next;
    });
  };

  const addCustomEmail = () => {
    const email = customEmail.trim();
    if (!email) return;
    if (!isValidEmail(email)) {
      alert('Please enter a valid email address.');
      return;
    }
    setRecipients((prev) => (prev.includes(email) ? prev : [...prev, email]));
    setCustomEmail('');
  };

  const removeRecipient = (email: string) => {
    setRecipients((prev) => prev.filter((e) => e !== email));
  };

  const copyRecipientsToClipboard = async () => {
    if (recipients.length === 0) { alert('No recipient emails to copy.'); return; }
    try {
      await navigator.clipboard.writeText(recipients.join(', '));
      alert('Recipient emails copied to clipboard.');
    } catch (err) {
      console.error('Could not copy recipient emails:', err);
      alert('Failed to copy emails to clipboard.');
    }
  };

  const clearRecipientsList = () => {
    if (recipients.length === 0) { alert('No recipient emails to clear.'); return; }
    setRecipients([]);
    alert('Cleared recipients list');
  };

  const handleSendClick = () => setShowConfirmation(true);
  const handleCancelSend = () => setShowConfirmation(false);

  const handleConfirmSend = async () => {
    setShowConfirmation(false);
    setLoading(true);
    try {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();

      const res = await fetch('/api/admin/email/dispatchSoftwareEmail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          recipients,
          templateId: selectedTemplateId,
          params: templateParams
        })
      });

      if (!res.ok) throw new Error('Send failed');
      alert('Emails queued for sending');
      setRecipients([]);
    } catch (e) {
      console.error('Error sending software update emails:', e);
      alert('Failed to send emails');
    } finally {
      setLoading(false);
    }
  };

  const popupProps: AdminConfirmationPopupProps = {
    innerHTML: (
      <div className="w-full h-full flex flex-col items-center justify-center gap-4">
        <p className="w-full text-lg text-center">
          Are you sure you want to send this software update email to the
          selected recipients?
        </p>
        <div className="w-full text-sm text-gray-700 pt-4">
          <p>
            <strong>Recipients:</strong> {recipientCount} email
            {recipientCount === 1 ? '' : 's'}
          </p>
          <p>
            <strong>Template:</strong> {selectedTemplate?.name || '—'}
          </p>
        </div>
      </div>
    ),
    onConfirm: handleConfirmSend,
    onCancel: handleCancelSend
  };

  if (loading) return <LoadingAnimatedInline />;

  return (
    <div className="p-6 max-w-4xl mx-auto h-full">
      <h2 className="text-2xl font-bold mb-6">Send Software Update Email</h2>

      <div className="grid gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Email Recipients
          </label>
          <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Add Group
              </label>
              <div className="flex gap-2 mt-1">
                <select
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                  className="flex-1 rounded-md border border-light-border p-2"
                >
                  {groups.length === 0 ? (
                    <option disabled value="">Loading groups...</option>
                  ) : (
                    groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))
                  )}
                </select>
                <button
                  type="button"
                  onClick={addGroupRecipients}
                  disabled={groups.length === 0}
                  className="px-4 py-2 bg-light-main text-white rounded-md hover:bg-light-main-hover transition-colors duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Adds all emails from the selected group into the recipients list.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Add Specific Email
              </label>
              <div className="flex gap-2 mt-1">
                <input
                  value={customEmail}
                  onChange={(e) => setCustomEmail(e.target.value)}
                  placeholder="name@example.com"
                  className="flex-1 rounded-md border border-light-border p-2"
                />
                <button
                  type="button"
                  onClick={addCustomEmail}
                  className="px-4 py-2 bg-light-main text-white rounded-md hover:bg-light-main-hover transition-colors duration-200 ease-in-out"
                >
                  Add
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Add a single email address to the recipient list.
              </p>
            </div>
          </div>

          <div className="mt-4 border border-light-border rounded bg-white p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                Current Recipients ({recipientCount})
              </span>
              <div className="gap-2 flex">
                <button
                  type="button"
                  onClick={copyRecipientsToClipboard}
                  disabled={recipientCount === 0}
                  className="px-2 py-1 bg-gray-200 text-sm text-gray-800 rounded-md hover:bg-gray-300 transition-colors duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Copy Emails
                </button>
                <button
                  type="button"
                  onClick={clearRecipientsList}
                  disabled={recipientCount === 0}
                  className="px-2 py-1 bg-gray-200 text-sm text-gray-800 rounded-md hover:bg-red-500 hover:text-light-text-alt transition-colors duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Clear Emails
                </button>
              </div>
            </div>
            {recipients.length === 0 ? (
              <p className="mt-2 text-sm text-gray-500">No recipients added yet.</p>
            ) : (
              <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                {recipients.map((email) => (
                  <li
                    key={email}
                    className="flex items-center justify-between rounded border border-light-border px-3 py-2"
                  >
                    <span className="text-sm break-all">{email}</span>
                    <button
                      type="button"
                      onClick={() => removeRecipient(email)}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Email Template
          </label>
          <select
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
            className="mt-1 block w-full rounded-md border border-light-border p-2"
          >
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Preview</label>
          <div className="mt-1 p-4 border border-light-border rounded bg-white h-48 overflow-auto">
            {selectedTemplate ? (
              <div dangerouslySetInnerHTML={{ __html: selectedTemplate.html }} />
            ) : (
              <p className="text-sm text-gray-500">No template selected</p>
            )}
          </div>
        </div>

        <div id="paramsContainer" className="flex flex-col gap-4 w-full">
          {selectedTemplate?.parameters.map((parameter) => (
            <div key={parameter} className="w-full">
              <p className="text-base font-medium text-gray-700 mb-2">
                {parameter.charAt(0).toUpperCase() + parameter.slice(1)}
              </p>
              <textarea
                value={templateParams[parameter] || ''}
                onChange={(e) =>
                  setTemplateParams({ ...templateParams, [parameter]: e.target.value })
                }
                placeholder={`Enter ${parameter}...`}
                className="w-full min-h-[120px] rounded-md border border-light-border p-3 text-base resize-y whitespace-pre-wrap"
              />
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <button
            onClick={handleSendClick}
            disabled={recipientCount === 0 || !selectedTemplate}
            className="px-4 py-2 bg-light-main text-white rounded-md hover:bg-light-main-hover transition-colors duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Send Software Update
          </button>
          <div className="flex items-center text-sm text-gray-600">
            Recipient Count:{' '}
            <span className="font-semibold ml-2">{recipientCount}</span>
          </div>
        </div>
      </div>

      {showConfirmation && <AdminConfirmationPopup {...popupProps} />}
    </div>
  );
};

export default EmailUpdate;